// =============================================================================
// Job Invoice (Bill) detail — faithful port of IMS Invoice_View / Invoice_Add,
// Invoice_WorkerHours, Invoice_Overhead, Invoice_CostProfit, Job_Cost_Profit.
// Shows customer snapshot, line items by type with per-type subtotals/discounts,
// whole-invoice discount, tax, total, deposit/remaining, worker (labor) hours,
// overhead rates, and actual cost / gross profit job-costing summary.
// =============================================================================
import { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Printer, TrendingUp, ClipboardCheck, FileText, X, Pencil, Users, Percent, Plus, Trash2, Save, Hammer } from 'lucide-react'
import {
  getBillWithItems, listWorkers, listLaborHours, addLaborHour, deleteLaborHour,
  saveBillOverhead, saveActualItems, type OverheadRates,
} from '../services/data'
import { PageHeader, Card, Spinner, Button, StatCard } from '../components/shared/ui'
import { customerLabel, currency, date } from '../lib/format'
import { printHtml } from '../lib/print'
import { invoiceReportHtml, REPORT_CSS } from '../lib/reports'
import { toast } from 'react-toastify'
import type { Bill, BillItem, LaborHour, Worker, Customer } from '../types'

const workerLabel = (w: Worker) => [w.last_name, w.first_name].filter(Boolean).join(', ') || `Worker #${w.legacy_id ?? '?'}`
const ACTUAL_TYPES = ['Plants', 'Materials', 'Labor', 'Rental', 'Hardgoods']

const TYPES = ['Plants', 'Materials', 'Labor', 'Rental', 'Hardgoods'] as const

function typeBucket(t: string | null): string {
  if (t === 'Hidden Cost') return 'Labor'
  return t ?? 'Other'
}

export default function BillDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [bill, setBill] = useState<Bill | null>(null)
  const [items, setItems] = useState<BillItem[]>([])
  const [labor, setLabor] = useState<LaborHour[]>([])
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [workers, setWorkers] = useState<Worker[]>([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState<'costprofit' | 'completion' | 'labor' | 'overhead' | null>(null)

  const load = useCallback(async () => {
    if (!id) return
    const res = await getBillWithItems(id)
    if (res) { setBill(res.bill); setItems(res.items); setLabor(res.labor); setCustomer(res.customer) }
  }, [id])

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)) }, [load])
  useEffect(() => { listWorkers(true).then(setWorkers).catch(() => {}) }, [])

  const afterEdit = async () => { await load(); setModal(null) }
  const workerName = (l: LaborHour) => {
    const w = workers.find(x => x.id === l.worker_id)
    return w ? workerLabel(w) : (l.legacy_worker_id != null ? `Worker #${l.legacy_worker_id}` : '—')
  }

  if (loading) return <Spinner label="Loading job invoice…" />
  if (!bill) return <div className="text-gray-500">Invoice not found.</div>

  // Committed (billed) line items, excluding actual-cost rows.
  const billed = items.filter(i => !i.is_actual)

  const typeTotals: Record<string, number> = {}
  for (const it of billed) {
    const b = typeBucket(it.item_type)
    typeTotals[b] = (typeTotals[b] ?? 0) + (it.total ?? 0)
  }
  const discFor = (t: string): { p: number; d: number } => {
    switch (t) {
      case 'Plants': return { p: bill.inv_plant_discount_p ?? 0, d: bill.inv_plant_discount_d ?? 0 }
      case 'Materials': return { p: bill.inv_material_discount_p ?? 0, d: bill.inv_material_discount_d ?? 0 }
      case 'Labor': return { p: bill.inv_labor_discount_p ?? 0, d: bill.inv_labor_discount_d ?? 0 }
      case 'Rental': return { p: bill.rental_discount_percent ?? 0, d: bill.rental_discount ?? 0 }
      default: return { p: 0, d: 0 }
    }
  }

  const wholeInvoice = bill.whole_invoice ?? Object.values(typeTotals).reduce((s, v) => s + v, 0)
  const wholeFinal = bill.disc_whole_invoice ?? wholeInvoice
  const taxPct = bill.sales_tax_percent ?? 0
  const taxAmt = bill.sales_tax_amount ?? 0
  const grand = bill.invoice_total ?? (wholeFinal + taxAmt)
  const deposit = bill.deposit ?? 0
  const remaining = bill.remaining_balance ?? (grand - deposit)

  // Job costing — actual cost prefers stored value; otherwise sum item cost + labor.
  const laborCost = labor.reduce((s, l) => s + (l.rate ?? 0) * (l.hours ?? 0), 0)
  const itemCost = items.filter(i => i.is_actual).reduce((s, i) => s + (i.cost ?? 0) * (i.qty ?? 0), 0)
    || items.reduce((s, i) => s + (i.cost ?? 0) * (i.qty ?? 0), 0)
  const actualCost = bill.actual_cost ?? (itemCost + laborCost)
  const grossProfit = bill.gross_profit ?? (grand - taxAmt - actualCost)
  const margin = grand - taxAmt > 0 ? (grossProfit / (grand - taxAmt)) * 100 : 0
  const laborHoursTotal = labor.reduce((s, l) => s + (l.hours ?? 0), 0)

  const presentTypes: string[] = [
    ...TYPES.filter(t => billed.some(i => typeBucket(i.item_type) === t)),
    ...Object.keys(typeTotals).filter(t => !TYPES.includes(t as typeof TYPES[number])),
  ]

  const custName = customer ? customerLabel(customer) : (bill.customer_name ?? 'Non-customer')
  const actualItems = items.filter(i => i.is_actual)

  // Faithful port of Invoice_View.btnPrint -> Invoice_report (letterhead,
  // customer block, items grouped by type with per-type subtotals, GUARANTEE-
  // style totals block with whole-invoice discount + deposit/balance).
  const printInvoice = () => {
    const body = invoiceReportHtml({ bill, items: billed, customer })
    printHtml(`Job Invoice ${bill.bill_code ?? bill.legacy_bill_id ?? ''}`, body, REPORT_CSS)
  }

  // Contractor copy: same invoice layout but with NO dollar amounts (no prices,
  // no totals/discounts) — mirrors the estimate "Russ Version".
  const printContractor = () => {
    const body = invoiceReportHtml({ bill, items: billed, customer, noPrices: true })
    printHtml(`Job Invoice ${bill.bill_code ?? bill.legacy_bill_id ?? ''} (Contractor)`, body, REPORT_CSS)
  }

  // Crew Work Order (port of WorkOrder_Report): job/site details + items by type
  // WITHOUT prices, plus assigned worker hours — for the field crew.
  const printWorkOrder = () => {
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    const byType = presentTypes.map(t => {
      const its = billed.filter(i => typeBucket(i.item_type) === t)
      if (its.length === 0) return ''
      return `<h3>${esc(t)}</h3><table><thead><tr><th>Qty</th><th>Size</th><th>Description</th></tr></thead><tbody>${
        its.map(i => `<tr><td class="qty">${i.qty ?? ''}</td><td>${esc(i.item_size ?? '')}</td><td>${esc(i.item_name ?? '')}</td></tr>`).join('')
      }</tbody></table>`
    }).join('')
    const crew = labor.length === 0 ? '' : `<h3>Assigned Crew</h3><table><thead><tr><th>Worker</th><th>Hours</th></tr></thead><tbody>${
      labor.map(l => `<tr><td>${esc(workerName(l))}</td><td class="qty">${(l.hours ?? 0).toFixed(2)}</td></tr>`).join('')
    }</tbody></table>`
    printHtml(`Work Order ${bill.bill_code ?? bill.legacy_bill_id ?? ''}`, `
      <div style="text-align:center"><h2>Work Order</h2><p>Job ${esc(String(bill.bill_code ?? bill.legacy_bill_id ?? ''))} · ${date(bill.bill_date)}</p></div>
      <div class="site"><b>${esc(custName)}</b><br/>${esc([bill.customer_address ?? customer?.address, bill.customer_city ?? customer?.city, bill.customer_state ?? customer?.state, bill.customer_zip ?? customer?.zip].filter(Boolean).join(', '))}<br/>${esc(bill.customer_phone1 ?? customer?.phone_home ?? '')}</div>
      ${(bill.comments || bill.comment) ? `<div class="notes"><b>Notes:</b> ${esc(String(bill.comments || bill.comment))}</div>` : ''}
      ${byType}${crew}
      <div class="sign">Crew Lead: ____________________  Date: __________  Hours: ________</div>`,
      `h2 { margin: 4px 0; } h3 { margin: 14px 0 4px; border-bottom: 2px solid #2f7d32; color: #1f5e22; }
       table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
       th, td { text-align: left; padding: 4px 6px; border: 1px solid #bbb; font-size: 13px; }
       .qty { width: 50px; text-align: center; } .site { margin: 10px 0; font-size: 14px; }
       .notes { margin: 8px 0; font-size: 13px; } .sign { margin-top: 28px; font-size: 13px; }`)
  }

  return (
    <div>
      <Button variant="ghost" onClick={() => navigate('/bills')} className="mb-4">
        <ArrowLeft size={16} /> Back to Job Invoices
      </Button>
      <PageHeader
        title={`Job Invoice ${bill.bill_code ?? `#${bill.legacy_bill_id ?? ''}`}`}
        subtitle={date(bill.bill_date)}
        actions={
          <>
            {bill.estimate_id && <Button variant="ghost" onClick={() => navigate(`/estimates/${bill.estimate_id}`)}><FileText size={16} /> Source Estimate</Button>}
            <Button variant="secondary" onClick={() => navigate(`/bills/${bill.id}/edit`)}><Pencil size={16} /> Edit</Button>
            <Button variant="secondary" onClick={() => setModal('completion')}><ClipboardCheck size={16} /> Job Completion</Button>
            <Button variant="secondary" onClick={() => setModal('costprofit')}><TrendingUp size={16} /> Cost/Profit Analysis</Button>
            <Button variant="secondary" onClick={printWorkOrder}><Hammer size={16} /> Work Order</Button>
            <Button variant="secondary" onClick={printContractor}><FileText size={16} /> Contractor</Button>
            <Button onClick={printInvoice}><Printer size={16} /> Print</Button>
          </>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Invoice Total" value={currency(grand)} accent="green" />
        <StatCard label="Remaining" value={currency(remaining)} accent="amber" />
        <StatCard label="Actual Cost" value={currency(actualCost)} accent="blue" />
        <StatCard label="Gross Profit" value={currency(grossProfit)} accent={grossProfit >= 0 ? 'purple' : 'red'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-5 lg:col-span-1">
          <h3 className="font-semibold text-gray-800 mb-3">Customer</h3>
          <div className="text-sm space-y-1 text-gray-700">
            <p className="font-medium">{custName}</p>
            <p>{[bill.customer_address ?? customer?.address, bill.customer_city ?? customer?.city, bill.customer_state ?? customer?.state, bill.customer_zip ?? customer?.zip].filter(Boolean).join(', ')}</p>
            {(bill.customer_phone1 || customer?.phone_home) && <p>{bill.customer_phone1 ?? customer?.phone_home}</p>}
            {customer && <button onClick={() => navigate(`/customers/${customer.id}`)} className="text-green-700 hover:underline text-xs mt-1">View customer →</button>}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-1 text-sm">
            {bill.is_retail_sale && <span className="inline-block px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs mr-1">Retail Sale</span>}
            {bill.is_spring_cleanup && <span className="inline-block px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs mr-1">Spring Cleanup</span>}
            {bill.is_actual && <span className="inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-xs">Actuals Entered</span>}
          </div>
          {(bill.comments || bill.comment) && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Comments</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{bill.comments || bill.comment}</p>
            </div>
          )}
        </Card>

        <div className="lg:col-span-2 space-y-4">
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-gray-600 border-b border-gray-200">
                    <th className="text-left font-semibold px-3 py-2">Inv ID</th>
                    <th className="text-left font-semibold px-3 py-2">Description</th>
                    <th className="text-left font-semibold px-3 py-2">Type</th>
                    <th className="text-right font-semibold px-3 py-2">Qty</th>
                    <th className="text-left font-semibold px-3 py-2">Size</th>
                    <th className="text-right font-semibold px-3 py-2">Price</th>
                    <th className="text-right font-semibold px-3 py-2">Disc$</th>
                    <th className="text-right font-semibold px-3 py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {billed.length === 0 ? (
                    <tr><td colSpan={8} className="text-center text-gray-400 py-10">No line items</td></tr>
                  ) : billed.map(it => (
                    <tr key={it.id} className="border-b border-gray-100">
                      <td className="px-3 py-1.5 text-gray-500">{it.legacy_inventory_code ?? '—'}</td>
                      <td className="px-3 py-1.5">{it.item_name}</td>
                      <td className="px-3 py-1.5">{it.item_type}</td>
                      <td className="px-3 py-1.5 text-right">{it.qty}</td>
                      <td className="px-3 py-1.5">{it.item_size}</td>
                      <td className="px-3 py-1.5 text-right">{currency(it.price)}</td>
                      <td className="px-3 py-1.5 text-right">{currency(it.discount_dollar)}</td>
                      <td className="px-3 py-1.5 text-right font-medium">{currency(it.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="font-semibold text-gray-800 mb-3">Totals</h3>
            <div className="space-y-1.5 text-sm">
              {presentTypes.map(t => {
                const sub = typeTotals[t] ?? 0
                const { p, d } = discFor(t)
                return (
                  <div key={t} className="flex justify-between text-gray-600">
                    <span>{t} {(p > 0 || d > 0) && <span className="text-xs text-gray-400">(disc {p > 0 ? `${p}%` : currency(d)})</span>}</span>
                    <span>{currency(sub - d)}</span>
                  </div>
                )
              })}
              <div className="flex justify-between text-gray-700 pt-2 border-t border-gray-200">
                <span>Whole Invoice</span><span>{currency(wholeInvoice)}</span>
              </div>
              {((bill.whole_invoice_discount_d ?? 0) > 0 || (bill.whole_invoice_discount_p ?? 0) > 0) && (
                <div className="flex justify-between text-gray-600">
                  <span>Invoice Discount {(bill.whole_invoice_discount_p ?? 0) > 0 ? `(${bill.whole_invoice_discount_p}%)` : ''}</span>
                  <span>-{currency((bill.whole_invoice_discount_d ?? 0) || (wholeInvoice - wholeFinal))}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-600">
                <span>Sales Tax ({(taxPct * 100).toFixed(3).replace(/\.?0+$/, '')}%)</span><span>{currency(taxAmt)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t border-gray-200">
                <span>Invoice Total</span><span>{currency(grand)}</span>
              </div>
              <div className="flex justify-between text-gray-600"><span>Deposit</span><span>{currency(deposit)}</span></div>
              <div className="flex justify-between font-semibold text-gray-800"><span>Remaining Balance</span><span>{currency(remaining)}</span></div>
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800">Worker Hours</h3>
              <Button variant="secondary" onClick={() => setModal('labor')}><Users size={15} /> Edit Worker Hours</Button>
            </div>
            {labor.length === 0 ? (
              <p className="text-sm text-gray-400 py-2">No worker hours recorded.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-500 border-b border-gray-200">
                    <th className="text-left font-medium px-2 py-1">Worker</th>
                    <th className="text-right font-medium px-2 py-1">Rate</th>
                    <th className="text-right font-medium px-2 py-1">Hours</th>
                    <th className="text-right font-medium px-2 py-1">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {labor.map(l => (
                    <tr key={l.id} className="border-b border-gray-100">
                      <td className="px-2 py-1 text-gray-600">{workerName(l)}</td>
                      <td className="px-2 py-1 text-right">{currency(l.rate)}</td>
                      <td className="px-2 py-1 text-right">{(l.hours ?? 0).toFixed(2)}</td>
                      <td className="px-2 py-1 text-right">{currency((l.rate ?? 0) * (l.hours ?? 0))}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold">
                    <td className="px-2 py-1">Total</td><td />
                    <td className="px-2 py-1 text-right">{laborHoursTotal.toFixed(2)}</td>
                    <td className="px-2 py-1 text-right">{currency(laborCost)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800">Job Costing</h3>
              <Button variant="secondary" onClick={() => setModal('overhead')}><Percent size={15} /> Edit Overhead</Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><p className="text-gray-500">Actual Cost</p><p className="font-semibold">{currency(actualCost)}</p></div>
              <div><p className="text-gray-500">Gross Profit</p><p className={`font-semibold ${grossProfit >= 0 ? 'text-green-700' : 'text-red-600'}`}>{currency(grossProfit)}</p></div>
              <div><p className="text-gray-500">Margin</p><p className="font-semibold">{margin.toFixed(1)}%</p></div>
              <div><p className="text-gray-500">Labor Hours</p><p className="font-semibold">{laborHoursTotal.toFixed(1)}</p></div>
            </div>
            {(bill.labor_oh || bill.other_oh || bill.cost_percentage_oh || bill.burden_rate_oh || bill.indirect_ir_oh) ? (
              <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs text-gray-600">
                <div><p className="text-gray-400">Labor OH</p>{(bill.labor_oh ?? 0)}%</div>
                <div><p className="text-gray-400">Other OH</p>{(bill.other_oh ?? 0)}%</div>
                <div><p className="text-gray-400">Cost % OH</p>{(bill.cost_percentage_oh ?? 0)}%</div>
                <div><p className="text-gray-400">Burden</p>{(bill.burden_rate_oh ?? 0)}%</div>
                <div><p className="text-gray-400">Indirect</p>{(bill.indirect_ir_oh ?? 0)}%</div>
              </div>
            ) : null}
          </Card>
        </div>
      </div>

      {modal === 'costprofit' && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-lg">Cost / Profit Analysis</h3>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="overflow-y-auto flex-1 p-4">
              {(
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div><p className="text-gray-500">Invoice (pre-tax)</p><p className="font-semibold">{currency(grand - taxAmt)}</p></div>
                    <div><p className="text-gray-500">Item Cost</p><p className="font-semibold">{currency(itemCost)}</p></div>
                    <div><p className="text-gray-500">Labor Cost</p><p className="font-semibold">{currency(laborCost)}</p></div>
                    <div><p className="text-gray-500">Actual Cost</p><p className="font-semibold">{currency(actualCost)}</p></div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm pt-3 border-t border-gray-100">
                    <div><p className="text-gray-500">Gross Profit</p><p className={`font-semibold text-lg ${grossProfit >= 0 ? 'text-green-700' : 'text-red-600'}`}>{currency(grossProfit)}</p></div>
                    <div><p className="text-gray-500">Margin</p><p className="font-semibold text-lg">{margin.toFixed(1)}%</p></div>
                    <div><p className="text-gray-500">Labor Hours</p><p className="font-semibold text-lg">{laborHoursTotal.toFixed(1)}</p></div>
                  </div>
                  <table className="w-full text-sm mt-2">
                    <thead className="bg-gray-50"><tr className="text-gray-600 border-b border-gray-200">
                      <th className="text-left font-semibold px-3 py-2">Item</th><th className="text-right font-semibold px-3 py-2">Qty</th>
                      <th className="text-right font-semibold px-3 py-2">Cost</th><th className="text-right font-semibold px-3 py-2">Price</th>
                      <th className="text-right font-semibold px-3 py-2">Profit</th>
                    </tr></thead>
                    <tbody>
                      {billed.map(it => {
                        const profit = (it.total ?? 0) - (it.cost ?? 0) * (it.qty ?? 0)
                        return (
                          <tr key={it.id} className="border-b border-gray-100">
                            <td className="px-3 py-1.5">{it.item_name}</td>
                            <td className="px-3 py-1.5 text-right">{it.qty}</td>
                            <td className="px-3 py-1.5 text-right">{currency(it.cost)}</td>
                            <td className="px-3 py-1.5 text-right">{currency(it.price)}</td>
                            <td className={`px-3 py-1.5 text-right ${profit >= 0 ? 'text-green-700' : 'text-red-600'}`}>{currency(profit)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {modal === 'labor' && (
        <LaborHoursEditor billId={bill.id} billCode={bill.bill_code ?? null} workers={workers}
          rows={labor} onClose={() => setModal(null)} onChanged={load} />
      )}
      {modal === 'overhead' && (
        <OverheadEditor billId={bill.id} bill={bill} onClose={() => setModal(null)} onSaved={afterEdit} />
      )}
      {modal === 'completion' && (
        <JobCompletionEditor billId={bill.id} billCode={bill.bill_code ?? null}
          rows={actualItems} onClose={() => setModal(null)} onSaved={afterEdit} />
      )}
    </div>
  )
}

// ---- Modal shell ------------------------------------------------------------
function ModalShell({ title, onClose, children, footer }: {
  title: string; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode
}) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-4">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">{footer}</div>}
      </Card>
    </div>
  )
}

// ---- Worker Hours editor (port of Invoice_WorkerHours) ----------------------
function LaborHoursEditor({ billId, billCode, workers, rows, onClose, onChanged }: {
  billId: string; billCode: string | null; workers: Worker[]; rows: LaborHour[]
  onClose: () => void; onChanged: () => Promise<void> | void
}) {
  const [list, setList] = useState<LaborHour[]>(rows)
  const [workerId, setWorkerId] = useState('')
  const [hours, setHours] = useState('')
  const [rate, setRate] = useState('')
  const [busy, setBusy] = useState(false)

  const refresh = async () => { setList(await listLaborHours(billId)); await onChanged() }
  const pickWorker = (wid: string) => {
    setWorkerId(wid)
    const w = workers.find(x => x.id === wid)
    if (w?.pay_rate != null) setRate(String(w.pay_rate))
  }
  const add = async () => {
    if (!workerId) { toast.error('Select a worker'); return }
    const h = parseFloat(hours); const r = parseFloat(rate)
    if (!(h > 0)) { toast.error('Enter hours'); return }
    setBusy(true)
    try {
      await addLaborHour({ bill_id: billId, bill_code: billCode, worker_id: workerId, rate: isNaN(r) ? 0 : r, hours: h })
      setWorkerId(''); setHours(''); setRate(''); await refresh()
    } catch { toast.error('Failed to add') } finally { setBusy(false) }
  }
  const remove = async (lid: string) => {
    setBusy(true)
    try { await deleteLaborHour(lid, billId); await refresh() }
    catch { toast.error('Failed to remove') } finally { setBusy(false) }
  }
  const wname = (l: LaborHour) => {
    const w = workers.find(x => x.id === l.worker_id)
    return w ? workerLabel(w) : (l.legacy_worker_id != null ? `Worker #${l.legacy_worker_id}` : '—')
  }
  const total = list.reduce((s, l) => s + (l.rate ?? 0) * (l.hours ?? 0), 0)
  const hoursTotal = list.reduce((s, l) => s + (l.hours ?? 0), 0)

  return (
    <ModalShell title="Worker Hours" onClose={onClose} footer={<Button onClick={onClose}>Done</Button>}>
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <label className="text-sm flex-1 min-w-[180px]">
          <span className="block text-gray-600 mb-1">Worker</span>
          <select value={workerId} onChange={e => pickWorker(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2">
            <option value="">Select worker…</option>
            {workers.map(w => <option key={w.id} value={w.id}>{workerLabel(w)}</option>)}
          </select>
        </label>
        <label className="text-sm w-24"><span className="block text-gray-600 mb-1">Hours</span>
          <input type="number" step="0.25" value={hours} onChange={e => setHours(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></label>
        <label className="text-sm w-28"><span className="block text-gray-600 mb-1">Rate</span>
          <input type="number" step="0.01" value={rate} onChange={e => setRate(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></label>
        <Button onClick={add} disabled={busy}><Plus size={15} /> Add</Button>
      </div>
      {list.length === 0 ? <p className="text-sm text-gray-400 py-2">No worker hours yet.</p> : (
        <table className="w-full text-sm">
          <thead><tr className="text-gray-500 border-b border-gray-200">
            <th className="text-left font-medium px-2 py-1">Worker</th>
            <th className="text-right font-medium px-2 py-1">Rate</th>
            <th className="text-right font-medium px-2 py-1">Hours</th>
            <th className="text-right font-medium px-2 py-1">Cost</th>
            <th className="w-8" />
          </tr></thead>
          <tbody>
            {list.map(l => (
              <tr key={l.id} className="border-b border-gray-100">
                <td className="px-2 py-1 text-gray-700">{wname(l)}</td>
                <td className="px-2 py-1 text-right">{currency(l.rate)}</td>
                <td className="px-2 py-1 text-right">{(l.hours ?? 0).toFixed(2)}</td>
                <td className="px-2 py-1 text-right">{currency((l.rate ?? 0) * (l.hours ?? 0))}</td>
                <td className="px-2 py-1 text-right"><button onClick={() => remove(l.id)} disabled={busy} className="text-gray-400 hover:text-red-600"><Trash2 size={15} /></button></td>
              </tr>
            ))}
            <tr className="font-semibold"><td className="px-2 py-1">Total</td><td /><td className="px-2 py-1 text-right">{hoursTotal.toFixed(2)}</td><td className="px-2 py-1 text-right">{currency(total)}</td><td /></tr>
          </tbody>
        </table>
      )}
    </ModalShell>
  )
}

// ---- Overhead editor (port of Invoice_Overhead) -----------------------------
function OverheadEditor({ billId, bill, onClose, onSaved }: {
  billId: string; bill: Bill; onClose: () => void; onSaved: () => Promise<void> | void
}) {
  const [r, setR] = useState<OverheadRates>({
    labor_oh: Number(bill.labor_oh ?? 0), other_oh: Number(bill.other_oh ?? 0),
    cost_percentage_oh: Number(bill.cost_percentage_oh ?? 0), burden_rate_oh: Number(bill.burden_rate_oh ?? 0),
    indirect_ir_oh: Number(bill.indirect_ir_oh ?? 0),
  })
  const [busy, setBusy] = useState(false)
  const set = (k: keyof OverheadRates, v: string) => setR(s => ({ ...s, [k]: parseFloat(v) || 0 }))
  const save = async () => {
    setBusy(true)
    try { await saveBillOverhead(billId, r); toast.success('Overhead saved'); await onSaved() }
    catch { toast.error('Failed to save overhead') } finally { setBusy(false) }
  }
  const fields: Array<{ k: keyof OverheadRates; label: string; hint: string }> = [
    { k: 'labor_oh', label: 'Labor OH', hint: 'Labor multiplier ($/hr)' },
    { k: 'other_oh', label: 'Other OH', hint: 'Business/job multiplier (%)' },
    { k: 'cost_percentage_oh', label: 'Cost % OH', hint: 'Cost percentage (%)' },
    { k: 'burden_rate_oh', label: 'Burden Rate', hint: 'Labor burden (%)' },
    { k: 'indirect_ir_oh', label: 'Indirect IR', hint: 'Indirect labor rate ($/hr)' },
  ]
  return (
    <ModalShell title="Overhead Rates" onClose={onClose}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy}><Save size={15} /> Save</Button></>}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.map(f => (
          <label key={f.k} className="text-sm">
            <span className="block text-gray-700 font-medium">{f.label}</span>
            <span className="block text-xs text-gray-400 mb-1">{f.hint}</span>
            <input type="number" step="0.01" value={r[f.k]} onChange={e => set(f.k, e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </label>
        ))}
      </div>
    </ModalShell>
  )
}

// ---- Job Completion editor (port of Invoice_JobCompletion) -------------------
function JobCompletionEditor({ billId, billCode, rows, onClose, onSaved }: {
  billId: string; billCode: string | null; rows: BillItem[]; onClose: () => void; onSaved: () => Promise<void> | void
}) {
  type R = { key: string; item_name: string; item_type: string; qty: string; cost: string }
  const [list, setList] = useState<R[]>(
    rows.map((it, i) => ({ key: `${i}`, item_name: it.item_name ?? '', item_type: it.item_type ?? '', qty: String(it.qty ?? ''), cost: String(it.cost ?? '') }))
  )
  const [busy, setBusy] = useState(false)
  const addRow = () => setList(l => [...l, { key: `n${Date.now()}`, item_name: '', item_type: '', qty: '1', cost: '' }])
  const upd = (key: string, field: keyof R, v: string) => setList(l => l.map(r => r.key === key ? { ...r, [field]: v } : r))
  const del = (key: string) => setList(l => l.filter(r => r.key !== key))
  const total = list.reduce((s, r) => s + (parseFloat(r.cost) || 0) * (parseFloat(r.qty) || 0), 0)
  const save = async () => {
    const payload = list.filter(r => r.item_name.trim()).map(r => ({
      item_name: r.item_name.trim(), item_type: r.item_type.trim() || null,
      qty: parseFloat(r.qty) || 0, cost: parseFloat(r.cost) || 0,
    }))
    setBusy(true)
    try { await saveActualItems(billId, billCode, payload); toast.success('Job completion saved'); await onSaved() }
    catch { toast.error('Failed to save') } finally { setBusy(false) }
  }
  return (
    <ModalShell title="Job Completion — Actual Costs" onClose={onClose}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy}><Save size={15} /> Save Actuals</Button></>}>
      <div className="mb-3"><Button variant="secondary" onClick={addRow}><Plus size={15} /> Add Item</Button></div>
      {list.length === 0 ? <p className="text-sm text-gray-400 py-2">No actual items. Add the real costs incurred to complete this job.</p> : (
        <table className="w-full text-sm">
          <thead><tr className="text-gray-500 border-b border-gray-200">
            <th className="text-left font-medium px-2 py-1">Description</th>
            <th className="text-left font-medium px-2 py-1">Type</th>
            <th className="text-right font-medium px-2 py-1 w-20">Qty</th>
            <th className="text-right font-medium px-2 py-1 w-28">Cost</th>
            <th className="text-right font-medium px-2 py-1 w-24">Ext.</th>
            <th className="w-8" />
          </tr></thead>
          <tbody>
            {list.map(r => (
              <tr key={r.key} className="border-b border-gray-100">
                <td className="px-1 py-1"><input value={r.item_name} onChange={e => upd(r.key, 'item_name', e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1" /></td>
                <td className="px-1 py-1">
                  <select value={r.item_type} onChange={e => upd(r.key, 'item_type', e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1">
                    <option value="">—</option>
                    {ACTUAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </td>
                <td className="px-1 py-1"><input type="number" step="0.01" value={r.qty} onChange={e => upd(r.key, 'qty', e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1 text-right" /></td>
                <td className="px-1 py-1"><input type="number" step="0.01" value={r.cost} onChange={e => upd(r.key, 'cost', e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1 text-right" /></td>
                <td className="px-2 py-1 text-right">{currency((parseFloat(r.cost) || 0) * (parseFloat(r.qty) || 0))}</td>
                <td className="px-1 py-1 text-right"><button onClick={() => del(r.key)} className="text-gray-400 hover:text-red-600"><Trash2 size={15} /></button></td>
              </tr>
            ))}
            <tr className="font-semibold"><td className="px-2 py-1" colSpan={4}>Total Actual Cost</td><td className="px-2 py-1 text-right">{currency(total)}</td><td /></tr>
          </tbody>
        </table>
      )}
    </ModalShell>
  )
}
